import React, { useEffect, useState, useCallback, useMemo } from "react";
import PrimaryButton from "../PrimaryButton";
import GreyButton from "../GreyButton";
import { useFormik } from "formik";
import { ReactComponent as AddIcon } from "../../../Assets/Icons/addNewIcon.svg";
import { ReactComponent as InfoIcon1 } from "../../../Assets/Icons/info.svg";
import { Tooltip } from "antd";
import { rulesOfferSchema } from "../../../utils/validationSchemas";
import CheckboxSelection from "../CheckboxSelection";
import Input from "../FormInputs";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentBusiness } from "../../../redux/slices/merchantSlice";
import {
  createOffer,
  createRule,
  getRules,
} from "../../../services/offerService";
import declinedIcon from "../../../Assets/Icons/declinedIcon.svg";
import pendingIcon from "../../../Assets/Icons/pendingIcon.svg";


const RulesRegulations = ({
  stepIndex,
  setStepIndex,
  handlePrevious,
  setPayloadData,
  editOfferData,
  isEdit,
  selectedMerchant
}) => {
  const dispatch = useDispatch();
  
  const loading = useSelector((state) => state?.offerReducer?.loading);
  const offerData = useSelector((state) => state?.offerReducer?.createdOffer);
  const role = useSelector((state) => state?.authReducer?.tokenData?.user?.role);
  const selectedBusiness = useSelector((state) => state?.merchantReducer?.selectedMerchent);
  
  // No direct store access; useSelector ensures safe snapshots

  const [addClicked, setAddClicked] = useState(false);
  const [allRules, setAllRules] = useState([]);
  const [predefinedRules, setPredefinedRules] = useState([]);
  const [customApprovedRules, setCustomApprovedRules] = useState([]);
  const [selectedRules, setSelectedRules] = useState([]);
  const [selectedRulesIds, setSelectedRulesIds] = useState([]);
  const [newRule, setNewRule] = useState("");
  const [offerPendingRule, setOfferPendingRule] = useState([]);
  
  const currentOfferType = offerData?.offerType || editOfferData?.offerType;
  
  // Use useMemo to prevent recalculation during reducer execution
  // For admin users, use the explicitly selected merchant so they can view/manage custom rules for that merchant
  const merchantIdForQuery = useMemo(() => {
    const adminSelected = (role === "admin" || role === "ADMIN") && selectedMerchant?._id;
    if (adminSelected) return selectedMerchant?._id;

    return selectedMerchant?._id ||
      offerData?.merchant?._id ||
      offerData?.merchantId ||
      editOfferData?.merchant?._id ||
      editOfferData?.merchantId;
  }, [role, selectedMerchant?._id, offerData?.merchant?._id, offerData?.merchantId, editOfferData?.merchant?._id, editOfferData?.merchantId]);

  useEffect(() => {
    if (role === "admin") {
      const biz =
        selectedMerchant ||
        offerData?.merchant ||
        editOfferData?.merchant ||
        (offerData?.merchantId ? { _id: offerData.merchantId } : null) ||
        (editOfferData?.merchantId ? { _id: editOfferData.merchantId } : null);
      if (biz && (!selectedBusiness || selectedBusiness?._id !== biz?._id)) {
        // Only set current business if we have a complete merchant object, not just an ID
        if (biz._id && (biz.businessName || biz.name)) {
          dispatch(setCurrentBusiness(biz));
        }
      }
    }
  }, [role, selectedMerchant, offerData?.merchant, offerData?.merchantId, editOfferData?.merchant, editOfferData?.merchantId, selectedBusiness, dispatch]);

  const fillUpForm = useCallback((data) => {
    if (data?.ruleAndRegulation) {
      const ruleObjects = [];
      const ruleIds = [];
      const ruleTitles = [];

      data?.ruleAndRegulation?.forEach((rule) => {
        ruleObjects.push(rule);
        ruleIds.push(rule?._id);
        ruleTitles.push(rule?.title);
        if (rule?.status === "pending" || rule?.status === "rejected") {
          setOfferPendingRule((prev) => [...prev, rule]);
        }
      });
      setSelectedRules(ruleObjects);
      setSelectedRulesIds(ruleIds);
    }
    
  }, []);

  useEffect(() => {
    if (editOfferData !== null && isEdit === true) {
      fillUpForm(editOfferData);
    } else {
      fillUpForm(offerData);
    }
  }, [offerData, editOfferData, isEdit, fillUpForm]);

  const getRulesData = useCallback(async () => {
    if (!currentOfferType) return;

    try {
      console.log("Fetching rules for offer type:", currentOfferType, "merchantId:", merchantIdForQuery);
      
      // Fetch predefined (no merchant) and custom (with merchant) in parallel
      const [generalRes, customRes] = await Promise.all([
        dispatch(getRules({ offerType: currentOfferType }))?.unwrap(),
        dispatch(
          getRules(
            merchantIdForQuery
              ? { offerType: currentOfferType, merchantId: merchantIdForQuery }
              : { offerType: currentOfferType }
          )
        )?.unwrap(),
      ]);

      console.log("Rules API responses:", { generalRes, customRes });

      // const generalApproved = Array.isArray(generalRes?.data)
      //   ? generalRes.data.filter((r) => r?.title)
      //   : [];
      // Only include predefined (non-custom) rules
const generalApproved = Array.isArray(generalRes?.data)
  ? generalRes.data.filter((r) => r?.title && r?.isCustom === false)
  : [];
console.log("General approved rules after filtering non-custom:", generalApproved);

      // Helper: dedupe by normalized title
      const uniqueByTitle = (arr) => {
        const seen = new Set();
        const out = [];
        for (const r of arr) {
          const key = String(r?.title || "").trim().toLowerCase();
          if (!key) continue;
          if (seen.has(key)) continue;
          seen.add(key);
          out.push(r);
        }
        return out;
      };

      // Prefer explicit merchantId, else derive by difference from general
      const customCandidates = Array.isArray(customRes?.data)
        ? customRes.data.filter((r) => r?.title)
        : [];
      const explicitCustom = customCandidates.filter(
        (r) => r?.merchantId || r?.merchantid || r?.merchant?._id || r?.isCustom === true
      );
      const generalTitles = new Set(generalApproved.map((r) => r.title));
      const derivedCustom = customCandidates.filter((r) => !generalTitles.has(r.title));
      const finalCustom = explicitCustom.length > 0 ? explicitCustom : derivedCustom;

      // Dedupe both lists by title
      const uniqueGeneral = uniqueByTitle(generalApproved);
      const uniqueCustom = uniqueByTitle(finalCustom);

      const customTitles = new Set((uniqueCustom || []).map((r) => String(r.title).trim().toLowerCase()));
      const cleanedPredefined = (uniqueGeneral || []).filter(
        (r) => !customTitles.has(String(r.title).trim().toLowerCase())
      );

      console.log("Processed rules:", { 
        cleanedPredefined: cleanedPredefined.length, 
        uniqueCustom: uniqueCustom.length,
        allRules: [...(cleanedPredefined || []), ...(uniqueCustom || [])].length
      });

      setPredefinedRules(cleanedPredefined);
      setCustomApprovedRules(uniqueCustom);
      setAllRules([...(cleanedPredefined || []), ...(uniqueCustom || [])]);
    } catch (error) {
      console.error("Error fetching rules:", error);
      // Set some default rules if API fails
      const defaultRules = [
        { _id: "default1", title: "Valid for one-time use only", offerType: currentOfferType },
        { _id: "default2", title: "Cannot be combined with other offers", offerType: currentOfferType },
        { _id: "default3", title: "Subject to availability", offerType: currentOfferType },
        { _id: "default4", title: "Management reserves the right to modify terms", offerType: currentOfferType }
      ];
      setPredefinedRules(defaultRules);
      setCustomApprovedRules([]);
      setAllRules(defaultRules);
    }
  }, [currentOfferType, merchantIdForQuery, dispatch]);

  useEffect(() => {
    // Use a longer timeout to ensure we're completely outside any reducer execution
    const timeoutId = setTimeout(async () => {
      if (!currentOfferType) return;
      await getRulesData();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [offerData?._id, editOfferData?._id, currentOfferType, merchantIdForQuery, getRulesData]);
  const predefinedOptions = Array.from(
    new Set((predefinedRules || []).map((d) => String(d?.title || "").trim()))
  );
  const customApprovedOptions = Array.from(
    new Set((customApprovedRules || []).map((d) => String(d?.title || "").trim()))
  );

  // console.log("Rule options:", { 
  //   predefinedRules: predefinedRules.length, 
  //   predefinedOptions: predefinedOptions,
  //   customApprovedRules: customApprovedRules.length,
  //   customApprovedOptions: customApprovedOptions,
  //   currentOfferType,
  //   selectedRules: selectedRules.map(r => r.title)
  // });
  const pendingCheckOptions = offerPendingRule
    ?.filter((r) => r?.status === "pending")
    .map((r) => r?.title);
  const rejectedCheckOptions = offerPendingRule
    ?.filter((r) => r?.status === "rejected")
    .map((r) => r?.title);

  const initialValues = {
    ruleAndRegulation: [],
  };
  useEffect(() => {
    // For admin users, use selectedMerchant ID; for others, use merchantIdForQuery
    const effectiveMerchantId = merchantIdForQuery || selectedMerchant?._id || null;
      
    setPayloadData({
      ruleAndRegulation: selectedRulesIds,
      id: offerData?._id,
      merchantId: effectiveMerchantId,
      isCreated: false,
    });
  }, [selectedRulesIds, offerData?._id, merchantIdForQuery, selectedMerchant?._id, role, setPayloadData]);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: rulesOfferSchema,

    onSubmit: async (values) => {
      const createPromise = await dispatch(createOffer({ 
        ...values,
        ruleAndRegulation: selectedRulesIds,
        offerId: isEdit ? editOfferData?._id : offerData?._id,
        merchantId: selectedMerchant?._id,
      }))?.unwrap();
      if (createPromise?.success === true) {
        // If it's an IN_STORE_OFFER, go to Location step (6), otherwise go to Review step (6)
        const stepNumber = role === "admin" ? 7 : 6;
        const nextStep = offerData?.offerType === "INSTORE_SWOODLE_OFFER" ? stepNumber : stepNumber;
        setStepIndex(nextStep);
      }
    },
  });

  const { ruleAndRegulation } = formik.values;

  const {
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
  } = formik;

  // Keep formik's checkbox value in sync with selectedRules when returning to this step
  useEffect(() => {
    const titles = Array.isArray(selectedRules)
      ? selectedRules.map((r) => r?.title).filter(Boolean)
      : [];
    setFieldValue("ruleAndRegulation", titles, false);
  }, [selectedRules, setFieldValue]);

  const attemptNext = async () => {
    // Ensure the rules field is marked as touched so errors show inline
    setFieldTouched("ruleAndRegulation", true, true);
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors || {}).length > 0) {
      const keys = Object.keys(validationErrors || {});
      if (keys.length) {
        const el = window.document.querySelector(`[name="${keys[0]}"]`);
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
      return;
    }
    handleSubmit();
  };

  const handleAddClick = () => {
    setAddClicked(true);
  };

  const handleNewRuleChange = (e) => {
    const value = e.target.value;
    setNewRule(value);
  };

  const handleNewRuleSubmit = async () => {
    const title = typeof newRule === "string" ? newRule.trim() : "";
    if (title !== "") {
      const payload = {
        title,
        offerId: offerData?._id,
        offerType: offerData?.offerType || editOfferData?.offerType,
        merchantId: merchantIdForQuery || selectedMerchant?._id || offerData?.merchantId || editOfferData?.merchantId,
      };
      const createPromise = await dispatch(createRule(payload))?.unwrap();
      if (createPromise?.success === true) {
        const newRuleData = createPromise?.data;
        
        if (role === "admin") {
          setSelectedRules((prev) => {
            const exists = prev?.some((r) => r?._id === newRuleData?._id);
            return exists ? prev : [...(prev || []), newRuleData];
          });
          setFieldValue("ruleAndRegulation", (() => {
            const titles = Array.isArray(ruleAndRegulation) ? ruleAndRegulation : [];
            return titles.includes(newRuleData?.title) ? titles : [...titles, newRuleData?.title];
          })());
          setSelectedRulesIds((prev) => {
            const ids = Array.isArray(prev) ? prev : [];
            return ids.includes(newRuleData._id) ? ids : [...ids, newRuleData._id];
          });
        } else {
          setOfferPendingRule((prev) => [...prev, newRuleData]);
        }
        
        setNewRule("");
        setAddClicked(false);
        
        await getRulesData();
      }
    }
  };
  const handleRuleChecked = (op) => {
    console.log("Rule checked:", op, "Current selected rules:", selectedRules.map(r => r.title));
    
    const isSelected = selectedRules.find((r) => r?.title === op);

    if (isSelected) {
      // Unselecting the rule
      const updatedRules = selectedRules.filter((r) => r?.title !== op);
      setSelectedRules(updatedRules);
      setFieldValue(
        "ruleAndRegulation",
        updatedRules?.map((u) => u?.title)
      );

      const updatedIds = selectedRulesIds.filter((ruleId) => {
        const rulefromAll = allRules.find((r) => r.title === op);
        const rulefromPending = offerPendingRule.find((r) => r.title === op);
        if (rulefromAll) {
          return rulefromAll._id !== ruleId;
        }
        if (rulefromPending) {
          return rulefromPending._id !== ruleId;
        }
        return true;
      });
      setSelectedRulesIds(updatedIds);
      console.log("Rule unselected:", op, "Updated rules:", updatedRules.map(r => r.title));
    } else {
      // Selecting the rule
      const rulefromAll = allRules.find((r) => r.title === op);
      const rulefromPending = !rulefromAll ? offerPendingRule.find((r) => r.title === op) : null;

      if (rulefromAll || rulefromPending) {
        const picked = rulefromAll || rulefromPending;
        setSelectedRules((prev) => {
          const exists = prev?.some((r) => r?._id === picked?._id);
          return exists ? prev : [...(prev || []), picked];
        });
        setFieldValue("ruleAndRegulation", (() => {
          const titles = Array.isArray(ruleAndRegulation) ? ruleAndRegulation : [];
          return titles.includes(picked?.title) ? titles : [...titles, picked?.title];
        })());
        setSelectedRulesIds((prev) => {
          const ids = Array.isArray(prev) ? prev : [];
          return ids.includes(picked?._id) ? ids : [...ids, picked?._id];
        });
        console.log("Rule selected:", op, "Updated rules:", [...(selectedRules || []), picked].map(r => r.title));
      }
    }
  };

  useEffect(() => { }, [predefinedOptions, customApprovedOptions]);

  const isNextButtonBusy = Boolean(loading);
  const isNextButtonDisabled = isNextButtonBusy || 
    selectedRulesIds.length === 0 || 
    !formik.values.ruleAndRegulation || 
    (Array.isArray(formik.values.ruleAndRegulation) && formik.values.ruleAndRegulation.length === 0);
  const nextButtonLabel = isNextButtonBusy ? (
    <span className="flex items-center justify-center gap-2">
      <span className="offer-btn-spinner" />
      <span>Processing...</span>
    </span>
  ) : (
    "Next"
  );

  return (
    <div className="w-full flex flex-col items-center justify-center mt-20">
      <div className="sm:w-[100%] md:w-[80%] lg:w-[70%] xl:w-[55%] offerform customize">
        <p className="font-poppins font-[500] sm:text-[12px] md:text-[14px] lg:text-[16px] text-[#303030]">
          Rules and Regulations
        </p>
        <div className="mt-4">
          {/* Show rules directly based on offer type from previous step; if missing, guide the user */}
          {currentOfferType ? (
            <>
              <CheckboxSelection
                options={predefinedOptions}
                divClassName="mt-1 w-full"
                internalDivClass="flex flex-col items-start w-full"
                secondRadioClass="mt-3 w-full"
                textClass="sm:text-[10px] md:text-[12px] lg:text-[14px] font-[400] text-[#303030] ml-4 w-full leading-6"
                checkClass="flex items-start w-full max-w-[560px]"
                label={""}
                width={"100%"}
                name="ruleAndRegulation"
                value={Array.isArray(ruleAndRegulation) ? ruleAndRegulation : []}
                error={errors.ruleAndRegulation}
                touched={touched.ruleAndRegulation}
                setFieldValue={setFieldValue}
                onChecked={handleRuleChecked}
                postIcon={null}
                twoColumns={true}
              />

              {customApprovedOptions?.length > 0 && (
                <>
                  <div className="h-[1px] bg-[#E6E6E6] w-full my-4" />
                  <p className="font-poppins font-[500] sm:text-[11px] md:text-[13px] lg:text-[15px] text-[#303030]">Custom Rules and Regulations</p>
                  <CheckboxSelection
                    options={customApprovedOptions}
                    divClassName="mt-1 w-full"
                    internalDivClass="flex flex-col items-start w-full"
                    secondRadioClass="mt-3 w-full"
                    textClass="sm:text-[10px] md:text-[12px] lg:text-[14px] font-[400] text-[#303030] ml-4 w-full leading-6"
                    checkClassName
                    checkClass="flex items-start w-full max-w-[560px]"
                    label={""}
                    width={"100%"}
                    name="ruleAndRegulation"
                    value={Array.isArray(ruleAndRegulation) ? ruleAndRegulation : []}
                    error={errors.ruleAndRegulation}
                    touched={touched.ruleAndRegulation}
                    setFieldValue={setFieldValue}
                    onChecked={handleRuleChecked}
                    postIcon={null}
                    twoColumns={true}
                  />
                </>
              )}
            </>
          ) : (
            <div className="bg-[#F6FBFE] border border-[#CDEFFB] text-[#086788] rounded-[6px] p-3 text-sm font-poppins">
              Please select an offer type in the "Create Offer" step to view the suggested Rules & Regulations.
            </div>
          )}
        {currentOfferType && offerPendingRule?.length > 0 && rejectedCheckOptions?.length > 0 && (
          <CheckboxSelection
            options={rejectedCheckOptions}
            divClassName="mt-1"
            internalDivClass="flex flex-col items-start"
            secondRadioClass="mt-3"
            textClass="sm:text-[10px] md:text-[12px] lg:text-[14px] font-[400] text-[#303030] ml-4"
            checkClass="flex items-start"
            label={""}
            width={"100%"}
            name="ruleAndRegulation"
            value={ruleAndRegulation}
            error={errors.ruleAndRegulation}
            touched={touched.ruleAndRegulation}
            setFieldValue={setFieldValue}
            onChecked={handleRuleChecked}
            postIcon={declinedIcon}
          />
        )}
        {currentOfferType && offerPendingRule?.length > 0 && pendingCheckOptions?.length > 0 && (
          <CheckboxSelection
            options={pendingCheckOptions}
            divClassName="mt-1"
            internalDivClass="flex flex-col items-start"
            secondRadioClass="mt-3"
            textClass="sm:text-[10px] md:text-[12px] lg:text-[14px] font-[400] text-[#303030] ml-4"
            checkClass="flex items-start"
            label={""}
            width={"100%"}
            name="ruleAndRegulation"
            value={ruleAndRegulation}
            error={errors.ruleAndRegulation}
            touched={touched.ruleAndRegulation}
            setFieldValue={setFieldValue}
            onChecked={handleRuleChecked}
            postIcon={pendingIcon}
          />
        )}
        {(role === "admin" || role === "merchant") && (
          <div className="mt-5 flex items-center gap-2">
            <button
              type="button"
              className="flex items-center gap-2 border-0 bg-transparent p-0 cursor-pointer"
              onClick={handleAddClick}
            >
              <AddIcon />
              <p className="font-poppins font-[400] sm:text-[12px] md:text-[14px] text-[#00ADEF] text-center leading-[18px]">
                Add Custom Rules and Regulations.
              </p>
            </button>
            <Tooltip
              title="Add your own rule that isn't listed above."
              color="#8C8C8C"
              placement="top"
            >
              <span
                className="flex items-center cursor-default"
                onClick={(e) => e.stopPropagation()}
              >
                <InfoIcon1 className="w-4 h-4 pointer-events-none info-icon-grey" />
              </span>
            </Tooltip>
          </div>
        )}
        {addClicked && (
          <div className="mt-5 w-full">
            <Input
              type="text"
              placeholder="Add Custom Rule"
              width="100%"
              radius="5px"
              InputDivClass="w-[100%] mb-4"
              name="newRule"
              value={newRule}
              handleChange={handleNewRuleChange}
              error=""
              touched={false}
            />
            <div className="w-full text-end">
              <PrimaryButton
                className="w-[40%] lg:w-[25%] h-[45px] rounded-[5px] text-[16px] font-[500] font-montserrat"
                label="Add"
                disabled={isNextButtonBusy || !newRule.trim()}
                onClick={handleNewRuleSubmit}
              />
            </div>
          </div>
        )}
        <div className="w-full text-end mt-20">
          <GreyButton
            className="w-[40%] lg:w-[25%] h-[45px] rounded-[5px] text-[16px] font-[500] font-montserrat"
            label="Previous"
            onClick={handlePrevious}
          />
          <PrimaryButton
            className={
              "w-[40%] lg:w-[25%] h-[45px] rounded-[5px] text-[16px] font-[500] font-montserrat ms-2"
            }
            label={nextButtonLabel}
            disabled={isNextButtonDisabled}
            onClick={attemptNext}
          />
        </div>
        </div>
      </div>
    </div>
  );
};

export default RulesRegulations;
